import { useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { useOpenConnectedAccountsSettings } from '@/settings-modal/hooks/useOpenConnectedAccountsSettings'
import { AuthContext } from '@/context/auth-context'
import { selectMatterById, selectMatterMembers } from '@/matter/store/selectors'
import { canEditMatter } from '@/matter/utils/matter-permissions'
import { matterPaths } from '@/routing/paths'
import { useAppSelector } from '@/store/store-hooks'

type UseMatterSyncRecoveryParams = {
  matterId: string | undefined
  connectedByUserId: string | null | undefined
  connectorFallbackLabel?: string | null
  settingsEntrySource: SettingsEntrySource
}

type MatterSyncRecovery = {
  isConnector: boolean
  connectorLabel: string | null
  onReconnect: () => void
  onOpenMatterSettings?: () => void
}

/** Resolves the role-aware recovery path for a matter's externally managed folder sync. */
export function useMatterSyncRecovery({
  matterId,
  connectedByUserId,
  connectorFallbackLabel,
  settingsEntrySource,
}: UseMatterSyncRecoveryParams): MatterSyncRecovery {
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()
  const openConnectedAccountsSettings = useOpenConnectedAccountsSettings()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const members = useAppSelector((state) => selectMatterMembers(state, matterId))
  const connectorMember = connectedByUserId ? members.find((member) => member.user_id === connectedByUserId) : null
  const connectorLabel = connectorMember?.display_name?.trim() || connectorMember?.email?.trim() || connectorFallbackLabel?.trim() || null
  const isConnector = connectedByUserId != null && connectedByUserId === user?.uid
  const canOpenMatterSettings = matterId != null && connectedByUserId != null && !isConnector && canEditMatter(matter)

  const onReconnect = useCallback(() => {
    openConnectedAccountsSettings(settingsEntrySource)
  }, [openConnectedAccountsSettings, settingsEntrySource])

  const openMatterSettings = useCallback(() => {
    if (matterId) navigate(matterPaths.settings(matterId))
  }, [matterId, navigate])

  return {
    isConnector,
    connectorLabel,
    onReconnect,
    onOpenMatterSettings: canOpenMatterSettings ? openMatterSettings : undefined,
  }
}
